import type { NgZone } from '@angular/core';
import { RepetitiveSubscription } from '@freelancer/decorators';
import type { Timer, TimeUtils } from '@freelancer/time-utils';
import * as Rx from 'rxjs';
import type { SockMessageSend } from './message-send-event.model';
import { Socket } from './sock-js';
import type { SockMessageRawEvent } from './sock-js.model';

export class NonCleanDisconnectError extends Error {}

/**
 * This class is responsible for to create an observable that
 * maintains the connection and send messages to the backend after
 * the websocket connection has benn established.
 */
export class ObservableWebSocket extends Rx.Observable<
  Rx.Observable<SockMessageRawEvent>
> {
  @RepetitiveSubscription()
  private messagesStreamSubscription?: Rx.Subscription;
  private socket: Socket;
  private heartBeatTimer: Timer;

  // Sock-js will send heartbeat every 25s
  private readonly HEARTBEAT_MISSING_THRESHOLD = 60;

  constructor(
    private webSocketUrl: string,
    private ngZone: NgZone,
    private timeUtils: TimeUtils,
    private handleMissingHeartBeat: () => void,
  ) {
    super();
  }

  _subscribe(
    observer: Rx.Subscriber<Rx.Observable<SockMessageRawEvent>>,
  ): () => void {
    return this.ngZone.runOutsideAngular(() => {
      this.socket = new Socket(this.webSocketUrl);
      let isClosed = false;
      let forcedClose = false;
      let heartbeatListener: EventListener;
      const serverResponseSubject$ = new Rx.Subject<SockMessageRawEvent>();

      this.socket.onopen = () => {
        this.ngZone.run(() => {
          if (forcedClose) {
            this.socket.close();
          } else {
            observer.next(serverResponseSubject$);
            heartbeatListener = this.heartbeatFactory();
            this.socket.addHeartBeatListener(heartbeatListener);
          }
        });
      };

      this.socket.onerror = e => {
        this.ngZone.run(() => {
          isClosed = true;
          observer.error(e);
        });
      };

      this.socket.onclose = e => {
        this.ngZone.run(() => {
          // prevent observer.complete() being called after observer.error(...)
          if (isClosed) {
            return;
          }

          isClosed = true;
          if (forcedClose || e.wasClean) {
            observer.complete();
            serverResponseSubject$.complete();
          } else if (!e.wasClean) {
            observer.error(new NonCleanDisconnectError());
          }
        });
      };

      this.socket.onmessage = e => {
        this.ngZone.run(() => {
          serverResponseSubject$.next(e);
        });
      };

      return (): void => {
        this.ngZone.run(() => {
          forcedClose = true;

          this.unsubscribe();

          if (!isClosed) {
            clearTimeout(this.heartBeatTimer);
            this.socket.removeHeartBeatListener(heartbeatListener);
            this.ngZone.runOutsideAngular(() => {
              this.socket.close();
            });
          }
        });
      };
    });
  }

  /**
   * This function is responsible for sending events to the
   * backend by subscribing to the messagesStream$ stream and whenever
   * this stream gets an emission we will send the emitted
   * value to the backend.
   *
   * @param messagesStream$
   */
  send(messagesStream$: Rx.Observable<SockMessageSend>): void {
    this.messagesStreamSubscription = messagesStream$.subscribe(data => {
      this.ngZone.runOutsideAngular(() => this.socket.send(data));
    });
  }

  private unsubscribe(): void {
    this.messagesStreamSubscription?.unsubscribe();
  }

  private heartbeatFactory(): EventListener {
    const createHeartbeatTimer = (): Timer =>
      this.timeUtils.setTimeout(
        // Reconnect the WebSocket
        () => this.handleMissingHeartBeat(),
        this.HEARTBEAT_MISSING_THRESHOLD * 1000,
      );
    this.heartBeatTimer = createHeartbeatTimer();
    return (event: Event) => {
      clearTimeout(this.heartBeatTimer);
      this.heartBeatTimer = createHeartbeatTimer();
    };
  }
}
